/**
 * Service Plans Service
 *
 * Manages service plan templates (NBN, Starlink, 4G, VoIP, etc.)
 * Used in Settings/Plans admin, intake forms, and quote builder
 *
 * Features:
 * - CRUD operations for service plans
 * - Query by service type
 * - Active plans filtering
 * - Sort by display order and promotion status
 * - Usage tracking (customer count)
 * - Margin calculations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '@/firebase';
import type { ServicePlan } from '@/types/schema';

const COLLECTION = 'service_plans';

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new service plan
 */
export async function createServicePlan(
  planData: Omit<ServicePlan, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName' | 'activeCustomers'>,
  userId: string,
  userName?: string
): Promise<string> {
  // Calculate margin if not provided
  const margin =
    planData.margin !== undefined
      ? planData.margin
      : calculateMargin(planData.costPrice, planData.sellPrice);

  const plan: Omit<ServicePlan, 'id'> = {
    ...planData,
    // V107.52.36 Phase 2 — Stamp the catalog kind discriminator so new
    // documents carry it explicitly. Legacy plans without `kind` are
    // treated as `'service'` by `getCatalogItemKind()`.
    kind: 'service',
    margin,
    activeCustomers: 0,
    createdBy: userId,
    createdByName: userName,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const docRef = await addDoc(collection(db, COLLECTION), plan);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single service plan by ID
 */
export async function getServicePlan(planId: string): Promise<ServicePlan | null> {
  const docRef = doc(db, COLLECTION, planId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as ServicePlan;
}

/**
 * Get all service plans (admin view)
 */
export async function getAllServicePlans(tenantId: string): Promise<ServicePlan[]> {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
    orderBy('serviceType', 'asc'),
    orderBy('sortOrder', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ServicePlan[];
}

/**
 * Get active service plans only (for intake forms/quote builder)
 * Returns only plans that are isActive = true
 * Sorted by promotion status and display order
 */
export async function getActiveServicePlans(
  tenantId: string,
  serviceType?: ServicePlan['serviceType']
): Promise<ServicePlan[]> {
  let q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
    where('isActive', '==', true)
  );

  if (serviceType) {
    q = query(q, where('serviceType', '==', serviceType));
  }

  const snapshot = await getDocs(q);
  const plans = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ServicePlan[];

  // Sort: promoted first, then by sortOrder
  return plans.sort((a, b) => {
    if (a.isPromoted && !b.isPromoted) return -1;
    if (!a.isPromoted && b.isPromoted) return 1;
    return (a.sortOrder || 999) - (b.sortOrder || 999);
  });
}

/**
 * Get service plans by service type
 */
export async function getServicePlansByType(
  tenantId: string,
  serviceType: ServicePlan['serviceType']
): Promise<ServicePlan[]> {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
    where('serviceType', '==', serviceType),
    orderBy('sortOrder', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ServicePlan[];
}

/**
 * Get promoted/featured plans
 */
export async function getPromotedPlans(tenantId: string): Promise<ServicePlan[]> {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
    where('isActive', '==', true),
    where('isPromoted', '==', true),
    orderBy('sortOrder', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ServicePlan[];
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update an existing service plan
 */
export async function updateServicePlan(
  planId: string,
  updates: Partial<Omit<ServicePlan, 'id' | 'createdAt' | 'createdBy' | 'createdByName'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION, planId);

  // Recalculate margin if pricing changed
  if (updates.costPrice !== undefined || updates.sellPrice !== undefined) {
    const currentPlan = await getServicePlan(planId);
    if (currentPlan) {
      const costPrice = updates.costPrice ?? currentPlan.costPrice;
      const sellPrice = updates.sellPrice ?? currentPlan.sellPrice;
      updates.margin = calculateMargin(costPrice, sellPrice);
    }
  }

  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Add an alias to a service plan for Better Proposals matching.
 * Next time a PDF item with this name is imported, it will auto-match.
 */
export async function addPlanAlias(planId: string, alias: string): Promise<void> {
  const docRef = doc(db, COLLECTION, planId);
  await updateDoc(docRef, {
    aliases: arrayUnion(alias),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Toggle plan active status
 */
export async function togglePlanActive(planId: string): Promise<void> {
  const plan = await getServicePlan(planId);
  if (!plan) {
    throw new Error('Plan not found');
  }

  await updateServicePlan(planId, {
    isActive: !plan.isActive,
  });
}

/**
 * Toggle plan promoted status
 */
export async function togglePlanPromoted(planId: string): Promise<void> {
  const plan = await getServicePlan(planId);
  if (!plan) {
    throw new Error('Plan not found');
  }

  await updateServicePlan(planId, {
    isPromoted: !plan.isPromoted,
  });
}

/**
 * Update plan sort order
 */
export async function updatePlanSortOrder(
  planId: string,
  sortOrder: number
): Promise<void> {
  await updateServicePlan(planId, { sortOrder });
}

/**
 * Increment active customer count
 */
export async function incrementPlanCustomerCount(planId: string): Promise<void> {
  const plan = await getServicePlan(planId);
  if (!plan) {
    throw new Error('Plan not found');
  }

  await updateServicePlan(planId, {
    activeCustomers: (plan.activeCustomers || 0) + 1,
  });
}

/**
 * Decrement active customer count
 */
export async function decrementPlanCustomerCount(planId: string): Promise<void> {
  const plan = await getServicePlan(planId);
  if (!plan) {
    throw new Error('Plan not found');
  }

  await updateServicePlan(planId, {
    activeCustomers: Math.max(0, (plan.activeCustomers || 0) - 1),
  });
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a service plan
 * NOTE: Only deletes if no active customers
 */
export async function deleteServicePlan(planId: string): Promise<void> {
  const plan = await getServicePlan(planId);

  if (!plan) {
    throw new Error('Plan not found');
  }

  if (plan.activeCustomers && plan.activeCustomers > 0) {
    throw new Error(
      `Cannot delete plan with ${plan.activeCustomers} active customers. Deactivate instead.`
    );
  }

  const docRef = doc(db, COLLECTION, planId);
  await deleteDoc(docRef);
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Calculate profit margin percentage
 */
export function calculateMargin(costPrice: number, sellPrice: number): number {
  if (sellPrice === 0) return 0;
  const margin = ((sellPrice - costPrice) / sellPrice) * 100;
  return Math.round(margin * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate Monthly Recurring Revenue (MRR) for a plan
 */
export function calculatePlanMRR(plan: ServicePlan): number {
  return (plan.activeCustomers || 0) * plan.sellPrice;
}

/**
 * Format plan display name with speed/data info
 */
export function formatPlanName(plan: ServicePlan): string {
  const parts: string[] = [plan.displayName || plan.planName];

  if (plan.downloadSpeed && plan.uploadSpeed) {
    parts.push(`${plan.downloadSpeed}/${plan.uploadSpeed}`);
  } else if (plan.downloadSpeed) {
    parts.push(plan.downloadSpeed);
  }

  if (plan.dataAllowance) {
    parts.push(plan.dataAllowance);
  }

  return parts.join(' - ');
}

/**
 * Validate plan data before create/update
 */
export function validateServicePlan(
  plan: Partial<ServicePlan>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!plan.serviceType) {
    errors.push('Service type is required');
  }

  if (!plan.planName || plan.planName.trim() === '') {
    errors.push('Plan name is required');
  }

  if (!plan.displayName || plan.displayName.trim() === '') {
    errors.push('Display name is required');
  }

  if (plan.costPrice === undefined || plan.costPrice < 0) {
    errors.push('Cost price must be >= 0');
  }

  if (plan.sellPrice === undefined || plan.sellPrice < 0) {
    errors.push('Sell price must be >= 0');
  }

  if (plan.costPrice !== undefined && plan.sellPrice !== undefined) {
    if (plan.sellPrice < plan.costPrice) {
      errors.push('Sell price cannot be less than cost price (negative margin)');
    }
  }

  if (!plan.tenantId) {
    errors.push('Tenant ID is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get statistics for all plans
 */
export async function getServicePlanStats(tenantId: string) {
  const plans = await getAllServicePlans(tenantId);

  const totalPlans = plans.length;
  const activePlans = plans.filter((p) => p.isActive).length;
  const promotedPlans = plans.filter((p) => p.isPromoted).length;
  const totalCustomers = plans.reduce((sum, p) => sum + (p.activeCustomers || 0), 0);
  const totalMRR = plans.reduce((sum, p) => sum + calculatePlanMRR(p), 0);

  const byServiceType = plans.reduce((acc, plan) => {
    if (!acc[plan.serviceType]) {
      acc[plan.serviceType] = {
        count: 0,
        activeCustomers: 0,
        mrr: 0,
      };
    }
    acc[plan.serviceType].count++;
    acc[plan.serviceType].activeCustomers += plan.activeCustomers || 0;
    acc[plan.serviceType].mrr += calculatePlanMRR(plan);
    return acc;
  }, {} as Record<string, { count: number; activeCustomers: number; mrr: number }>);

  return {
    totalPlans,
    activePlans,
    promotedPlans,
    totalCustomers,
    totalMRR,
    byServiceType,
  };
}
